/**
 * Mock state builders for the Appointment page.
 *
 * These provide appointment and patient metadata when navigating from
 * the Client Chart or Calendar. Note data (QuestionnaireResponses) is
 * no longer included here — it is always fetched from Medplum.
 *
 * When there is no Medplum server data, getMockAppointmentPageStateByAppointmentId
 * provides fallback mock state for appointment IDs that exist in the chart mock data.
 */
import type { Appointment, Patient } from '@medplum/fhirtypes';
import type { Client } from '../caseload/data';
import { mockClients } from '../caseload/data';
import type { Appointment as ChartAppointment } from '../clients/data';
import { mockAppointments, mockEpisodes } from '../clients/data';
import {
  getClientChartData,
  getClientEpisodes,
  getRelationshipForAppointment,
} from '../../data/clientChartApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import type { LinkedAppointmentInfo } from './types';
export type { LinkedAppointmentInfo };

export type AppointmentBreadcrumbFrom = 'client-chart' | 'calendar';

export interface AppointmentPageMockState {
  patient: Patient;
  appointment: Appointment;
  previousAppointment: LinkedAppointmentInfo | null;
  nextAppointment: LinkedAppointmentInfo | null;
  appointmentsLeft: number | null;
  hasSupervisor: boolean;
  /** Episode of care this appointment belongs to (from Medical Record / chart). */
  episodeId?: string;
  /** Display label for the episode, e.g. "Individual Therapy (Started Jan 15, 2024)". */
  episodeDisplayName?: string;
  /** Pronouns (e.g. "she/her") from client/chart context. */
  pronouns?: string;
  /** Where the user navigated from; used to show correct breadcrumb (Calendar vs Client Chart → Medical Record). */
  breadcrumbFrom?: AppointmentBreadcrumbFrom;
  /**
   * Cross-episode appointment view: session metadata only (no clinical documentation).
   * @deprecated Prefer `appointmentOnly`; kept for in-flight navigation state.
   */
  documentationOnly?: boolean;
  /** Cross-episode appointment view: session metadata only (no clinical documentation). */
  appointmentOnly?: boolean;
  /** Actual session duration in minutes (for completed sessions). */
  sessionDurationMinutes?: number;
}

function formatEpisodeDate(dateStr: string): string {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getEpisodeDisplayNameFromId(episodeId: string | undefined): string | undefined {
  if (!episodeId) return undefined;
  const episode = mockEpisodes.find((e) => e.id === episodeId);
  if (!episode) return undefined;
  const start = formatEpisodeDate(episode.startDate);
  if (episode.endDate) {
    return `${episode.name} (${start} – ${formatEpisodeDate(episode.endDate)})`;
  }
  return episode.name;
}

// ---------------------------------------------------------------------------
// Chart → FHIR converters
// ---------------------------------------------------------------------------

function toFhirStatus(status: ChartAppointment['status']): Appointment['status'] {
  const map: Record<ChartAppointment['status'], Appointment['status']> = {
    Scheduled: 'booked',
    Requested: 'proposed',
    Completed: 'fulfilled',
    'No-Show': 'noshow',
    'Client Canceled': 'cancelled',
    'Clinician Canceled': 'cancelled',
    Cancelled: 'cancelled',
  };
  return map[status] ?? 'booked';
}

function clientToFhirPatient(client: Client): Patient {
  const preferredGiven = (client.preferredFirstName ?? '').trim().split(/\s+/).filter(Boolean);
  const preferredFamily = (client.preferredLastName ?? '').trim();
  const legalGiven = (client.firstName ?? client.name.split(' ')[0] ?? '').trim().split(/\s+/).filter(Boolean);
  const legalFamily = (client.lastName ?? client.name.split(' ').slice(1).join(' ') ?? '').trim();

  const names: Patient['name'] = [];
  if (preferredGiven.length || preferredFamily) {
    names.push({ use: 'usual', given: preferredGiven.length ? preferredGiven : undefined, family: preferredFamily || undefined });
  }
  if (legalGiven.length || legalFamily) {
    names.push({ use: 'official', given: legalGiven.length ? legalGiven : undefined, family: legalFamily || undefined });
  }
  if (names.length === 0) {
    names.push({ given: [client.name], family: undefined });
  }

  return {
    resourceType: 'Patient',
    id: client.id,
    name: names,
    birthDate: client.dateOfBirth,
    telecom: [
      ...(client.phone ? [{ system: 'phone' as const, value: client.phone }] : []),
      ...(client.email ? [{ system: 'email' as const, value: client.email }] : []),
    ],
    active: client.status === 'Active',
  };
}

function clientChartDisplayName(client: Client): string {
  return client.preferredFirstName && client.preferredLastName
    ? `${client.preferredFirstName} ${client.preferredLastName}`
    : client.name;
}

function chartAppointmentToFhir(apt: ChartAppointment, patientId: string, patientDisplay: string): Appointment {
  const [startHour, startMin] = parseTime(apt.time);
  const start = new Date(apt.date);
  start.setHours(startHour, startMin, 0, 0);
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + 60);

  const relationship = getRelationshipForAppointment(apt.id);
  const noShowIds = new Set(apt.noShowMemberIds ?? []);
  const patientParticipants = relationship
    ? relationship.memberPatientIds.map((memberId) => {
        const memberClient = mockClients.find((c) => c.id === memberId);
        const display = memberClient ? clientChartDisplayName(memberClient) : memberId;
        // Members in noShowMemberIds are marked 'declined' so they are excluded from attendees
        const participantStatus: 'accepted' | 'declined' = noShowIds.has(memberId) ? 'declined' : 'accepted';
        return { actor: { reference: `Patient/${memberId}`, display }, status: participantStatus };
      })
    : [{ actor: { reference: `Patient/${patientId}`, display: patientDisplay }, status: 'accepted' as const }];

  return {
    resourceType: 'Appointment',
    id: apt.id,
    status: toFhirStatus(apt.status),
    start: start.toISOString(),
    end: end.toISOString(),
    participant: [
      ...patientParticipants,
      { actor: { reference: 'Practitioner/mock', display: apt.provider }, status: 'accepted' },
    ],
    appointmentType: { text: apt.serviceType },
    serviceType: [{ text: apt.serviceType }],
  };
}

function parseTime(timeStr: string): [number, number] {
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return [9, 0];
  let hour = parseInt(match[1], 10);
  const min = parseInt(match[2], 10);
  if (match[3].toUpperCase() === 'PM' && hour !== 12) hour += 12;
  if (match[3].toUpperCase() === 'AM' && hour === 12) hour = 0;
  return [hour, min];
}

function appointmentSortKey(apt: ChartAppointment): number {
  const [h, m] = parseTime(apt.time);
  const d = new Date(apt.date);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

function getPreviousNextAppointments(
  appointments: ChartAppointment[],
  currentId: string,
): { previous: LinkedAppointmentInfo | null; next: LinkedAppointmentInfo | null } {
  const sorted = [...appointments].sort((a, b) => appointmentSortKey(a) - appointmentSortKey(b));
  const currentIdx = sorted.findIndex((a) => a.id === currentId);
  if (currentIdx < 0) return { previous: null, next: null };

  const toInfo = (apt: ChartAppointment): LinkedAppointmentInfo => ({
    id: apt.id,
    date: formatDate(apt.date),
    time: apt.time,
    status: apt.status,
  });

  const previous = currentIdx > 0 ? toInfo(sorted[currentIdx - 1]) : null;
  const next = currentIdx >= 0 && currentIdx < sorted.length - 1 ? toInfo(sorted[currentIdx + 1]) : null;
  return { previous, next };
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

/**
 * Build mock state for the Appointment page from client chart data.
 * Note data is NOT included — it is fetched from Medplum via useAppointmentResponses.
 * Pass episodeDisplayName when opening from Medical Record so the header can show the episode.
 */
export function buildMockAppointmentPageState(
  client: Client,
  chartAppointment: ChartAppointment,
  allAppointments: ChartAppointment[],
  options?: { episodeDisplayName?: string; documentationOnly?: boolean; appointmentOnly?: boolean },
): AppointmentPageMockState {
  const patient = clientToFhirPatient(client);
  const patientDisplay = client.preferredFirstName && client.preferredLastName
    ? `${client.preferredFirstName} ${client.preferredLastName}`
    : client.name;

  const appointment = chartAppointmentToFhir(chartAppointment, client.id, patientDisplay);

  const { previous: previousAppointment, next: nextAppointment } = getPreviousNextAppointments(
    allAppointments,
    chartAppointment.id,
  );

  const episodeId = chartAppointment.episodeId;
  const episodeDisplayName = options?.episodeDisplayName
    ?? getEpisodeDisplayNameFromClientEpisodes(episodeId, client.id)
    ?? getEpisodeDisplayNameFromId(episodeId);

  const isCompleted = chartAppointment.status === 'Completed';

  return {
    patient,
    appointment,
    previousAppointment,
    nextAppointment,
    appointmentsLeft: null,
    hasSupervisor: false,
    breadcrumbFrom: 'client-chart',
    ...(episodeId && { episodeId }),
    ...(episodeDisplayName && { episodeDisplayName }),
    ...((options?.appointmentOnly || options?.documentationOnly) && { appointmentOnly: true }),
    ...(client.pronouns && { pronouns: client.pronouns }),
    ...(isCompleted && { sessionDurationMinutes: 53 }),
  };
}

/**
 * Fallback mock state for the Appointment page when Medplum has no server data.
 * Searches all clients' per-client chart data for the given appointment ID,
 * returning mock state for the correct client. Falls back to Sarah's data
 * for legacy appointment IDs (backward compat).
 */
export function getMockAppointmentPageStateByAppointmentId(
  appointmentId: string,
): AppointmentPageMockState | null {
  // First try legacy global data (Sarah Johnson's appointments)
  const legacyAppointment = mockAppointments.find((a) => a.id === appointmentId);
  if (legacyAppointment && mockClients.length > 0) {
    return buildMockAppointmentPageState(mockClients[0], legacyAppointment, mockAppointments);
  }

  // Search per-client data for the appointment
  for (const client of mockClients) {
    const chartData = getClientChartData(client.id);
    const apt = chartData.appointments.find((a) => a.id === appointmentId);
    if (apt) {
      const episodeDisplayName = getEpisodeDisplayNameFromClientEpisodes(apt.episodeId, client.id);
      return buildMockAppointmentPageState(client, apt, chartData.appointments, { episodeDisplayName });
    }
  }

  return null;
}

function getEpisodeDisplayNameFromClientEpisodes(episodeId: string | undefined, clientId: string): string | undefined {
  if (!episodeId) return undefined;
  const episodes = getClientEpisodes(clientId);
  const episode = episodes.find((e) => e.id === episodeId);
  if (!episode) return getEpisodeDisplayNameFromId(episodeId);
  return episode.name;
}

/** Minimal calendar event shape used when navigating from the provider calendar */
export interface CalendarEventForMock {
  id: string;
  title: string;
  start: Date;
  end: Date;
  client?: string;
  provider?: string;
  status?: string;
}

const FHIR_APPOINTMENT_STATUS_DEFAULT: Appointment['status'] = 'booked';

function calendarStatusToFhir(status: string | undefined): Appointment['status'] {
  const map: Record<string, Appointment['status']> = {
    proposed: 'proposed',
    confirmed: 'booked',
    completed: 'fulfilled',
    'clinician-cancelled': 'cancelled',
    'client-cancelled': 'cancelled',
    'client-late-cancelled': 'cancelled',
    'client-no-show': 'noshow',
    'entered-in-error': 'cancelled',
  };
  const mapped = status ? map[status] : undefined;
  return mapped ?? FHIR_APPOINTMENT_STATUS_DEFAULT;
}

/**
 * Build mock state for the Appointment page from a calendar event.
 */
export function buildMockAppointmentPageStateFromCalendarEvent(
  event: CalendarEventForMock,
): AppointmentPageMockState {
  const patientId = `${event.id}-patient`;
  const displayName = event.client ?? event.title ?? 'Client';

  const patient: Patient = {
    resourceType: 'Patient',
    id: patientId,
    name: [{ given: [displayName], family: undefined }],
  };

  const appointment: Appointment = {
    resourceType: 'Appointment',
    id: event.id,
    status: calendarStatusToFhir(event.status),
    start: event.start.toISOString(),
    end: event.end.toISOString(),
    participant: [
      { actor: { reference: `Patient/${patientId}`, display: displayName }, status: 'accepted' },
      { actor: { reference: 'Practitioner/mock', display: event.provider ?? 'Provider' }, status: 'accepted' },
    ],
    appointmentType: { text: event.title },
    serviceType: [{ text: event.title }],
  };

  return {
    patient,
    appointment,
    previousAppointment: null,
    nextAppointment: null,
    appointmentsLeft: null,
    hasSupervisor: false,
    breadcrumbFrom: 'calendar',
  };
}
